import { z } from 'zod';
import { ROLLER } from './izinler.js';
import { bosluklariTopla, cumle, eposta as epostaDuzen, iban as ibanDuzen, kategoriAdi, markaModel, ozelAd, plaka as plakaDuzen, telefon as telefonDuzen, urunAdi, } from './yaziDuzeni.js';
import { DOSYA_TURLERI, HAREKET_TURLERI, IS_EMRI_DURUMLARI, KALEM_TURLERI, ODEME_DURUMLARI, ODEME_TIPLERI, } from './tipler.js';
/**
 * DOĞRULAMA
 * Aynı şemalar hem sunucuda (zorunlu kontrol) hem arayüzde (anında geri
 * bildirim) kullanılır. Sunucu istemciye asla güvenmez; her istek burada
 * yeniden doğrulanır.
 */
const metin = (max = 200) => z.string().trim().max(max);
const zorunluMetin = (max = 200) => z.string().trim().min(1, 'Zorunlu alan').max(max);
/**
 * YAZIM DÜZENİ — sunucu tarafı güvencesi.
 *
 * Arayüz alandan çıkınca zaten düzeltiyor; burada bir kez daha düzeltiyoruz
 * ki API'ye doğrudan gönderilen, içe aktarılan ya da eski istemciden gelen
 * kayıtlar da düzgün yazılsın. Düzeltme yalnızca harf büyüklüğünü ve fazla
 * boşluğu değiştirir; içerik kaybolmaz.
 */
const ad = (max = 200) => zorunluMetin(max).transform(ozelAd);
const adBos = (max = 200) => metin(max).default('').transform(ozelAd);
const urun = (max = 200) => zorunluMetin(max).transform(urunAdi);
const urunBos = (max = 200) => metin(max).default('').transform(urunAdi);
const kategori = (max = 80) => metin(max).default('').transform(kategoriAdi);
const marka = (max = 80) => metin(max).default('').transform(markaModel);
const aciklama = (max = 1000) => metin(max).default('').transform(cumle);
const kod = (max = 40) => metin(max).default('').transform((v) => bosluklariTopla(v).toUpperCase());
const tutar = z.coerce.number().finite().min(0, 'Tutar negatif olamaz').max(100_000_000);
/**
 * PARA BİRİMİ ALANI — ve `tutar`ın ANLAMI.
 *
 * İSTEK gövdesindeki `tutar` (ve `birim_fiyat`, `alis_fiyat`, `satis_fiyat`)
 * KULLANICININ YAZDIĞI SAYIDIR ve `para_birimi` cinsindendir. Kullanıcı
 * "€ 400" yazdıysa gövdede `{ para_birimi: 'EUR', tutar: 400 }` gider.
 *
 * VERİTABANINDAKİ aynı adlı sütun ise TL KARŞILIĞINI tutar (18.240).
 * Çeviriyi tek bir yer yapar: `depo/paraCevir.ts`. İstemci asla TL
 * karşılığı hesaplayıp göndermez — gönderse bile yok sayılır, çünkü kur
 * kararı sunucunundur.
 *
 * Alan YOKSA 'TRY' varsayılır: göç öncesi yazılmış her istemci, her test ve
 * doğrudan API'ye atılan her istek hiç değişmeden çalışmaya devam eder.
 */
const paraBirimi = z.enum(['TRY', 'USD', 'EUR']).default('TRY');
/**
 * İSTEĞE BAĞLI kur. Kullanıcıdan İSTENMEZ — istek: "güncel kur yazmaya
 * gerek yok". Sunucu günün kurunu kendisi bulur. Alan yine de vardır:
 * kullanıcı özellikle düzeltmek isterse ("bankadan 46,10'dan bozdurdum")
 * o değeri gönderebilsin diye. Boş bırakılırsa sunucu kararı geçerlidir.
 */
const kur = z.coerce.number().finite().positive().max(1_000_000).nullable().optional();
const tarih = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Tarih GG.AA.YYYY olmalı');
const saat = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).or(z.literal(''));
const kimlik = z.coerce.number().int().positive();
/* ---------- Oturum ---------- */
export const GirisSemasi = z.object({
    kullanici_adi: zorunluMetin(60),
    sifre: z.string().min(1, 'Şifre gerekli').max(200),
});
export const SifreDegistirSemasi = z.object({
    mevcut: z.string().min(1),
    yeni: z.string().min(8, 'Şifre en az 8 karakter olmalı').max(200),
});
/* ---------- Kullanıcı ---------- */
export const KullaniciSemasi = z.object({
    kullanici_adi: zorunluMetin(60).regex(/^[a-z0-9._-]+$/i, 'Yalnızca harf, rakam, nokta ve tire'),
    ad_soyad: ad(120),
    rol: z.enum(ROLLER),
    aktif: z.union([z.literal(0), z.literal(1)]).default(1),
});
export const KullaniciOlusturSemasi = KullaniciSemasi.extend({
    sifre: z.string().min(8, 'Şifre en az 8 karakter olmalı').max(200),
});
export const KullaniciGuncelleSemasi = KullaniciSemasi.partial().extend({
    sifre: z.string().min(8).max(200).optional().or(z.literal('')),
});
/* ---------- Müşteri & Araç ---------- */
export const MusteriSemasi = z.object({
    ad: ad(160),
    telefon: metin(40).default('').transform(telefonDuzen),
    eposta: metin(160).default('').transform(epostaDuzen),
    not: aciklama(2000),
    /* Yalnızca yeni kayıtta kullanılır: devreden bakiye. */
    baslangic_bakiye: tutar.default(0),
    bakiye_yonu: z.enum(['borc', 'alacak']).default('borc'),
});
export const AracSemasi = z.object({
    musteri_id: kimlik,
    plaka: metin(20).default('').transform(plakaDuzen),
    marka: marka(60),
    model: marka(80),
    yil: z.coerce.number().int().min(1950).max(2100).nullable().optional(),
    motor: marka(80),
    motor_kodu: kod(40),
    sanziman: urunBos(40),
    vin: kod(24),
    not: aciklama(2000),
});
/**
 * YENİ MÜŞTERİ — İSTEĞE BAĞLI İLK ARAÇLA BİRLİKTE.
 *
 * Atölye akışı "önce müşteri, sonra araç" değildir: araç gelir, plakası
 * yazılır, sahibi kimse o müşteri açılır. Arayüz iki ayrı istek atarsa
 * (POST /musteriler, sonra POST /araclar) ikincisi düşünce ortada
 * PLAKASIZ bir müşteri kalır ve kimse fark etmez. Bu yüzden araç bilgisi
 * müşteriyle AYNI istekte, aynı veritabanı işleminde yazılır.
 *
 * `musteri_id` burada YOKTUR: henüz oluşmamıştır, sunucu doldurur.
 * Plaka boşsa araç hiç oluşturulmaz — alan isteğe bağlıdır.
 */
export const IlkAracSemasi = AracSemasi.omit({ musteri_id: true });
export const MusteriOlusturSemasi = MusteriSemasi.extend({
    arac: IlkAracSemasi.optional().nullable(),
});
/* ---------- İş Emri ---------- */
export const IsEmriKalemSemasi = z.object({
    tur: z.enum(KALEM_TURLERI),
    hizmet_id: kimlik.nullable().optional(),
    urun_id: kimlik.nullable().optional(),
    aciklama: zorunluMetin(200),
    adet: z.coerce.number().positive().max(100_000).default(1),
    birim_fiyat: tutar.default(0),
    /* Kalem başına para birimi. Kur BELGE ANINDA donar: müşteriye verilen
       rakam sonradan kendiliğinden değişmemelidir. */
    para_birimi: paraBirimi,
    kur,
});
export const IsEmriSemasi = z.object({
    musteri_id: kimlik,
    /* ARAÇ İSTEĞE BAĞLI. Plakası elinde olmayan ya da yalnız parça/hizmet
       alan müşteriye de iş emri açılabilir; araç sonradan eklenebilir.
       Veritabanında `is_emirleri.arac_id` NULL kabul eder (göç 15/G6). */
    arac_id: kimlik.nullable().optional(),
    islem: aciklama(400),
    not: aciklama(4000),
    kilometre: z.coerce.number().int().min(0).max(10_000_000).nullable().optional(),
    tutar: tutar.default(0),
    durum: z.enum(IS_EMRI_DURUMLARI).default('Bekliyor'),
    odeme_durumu: z.enum(ODEME_DURUMLARI).default('Odenmedi'),
    kalemler: z.array(IsEmriKalemSemasi).max(100).default([]),
});
/** Araç geldiğinde tek ekranda iş emri açmak için: müşteri ve araç da aynı anda yaratılabilir. */
export const HizliIsEmriSemasi = z.object({
    musteri_id: kimlik.nullable().optional(),
    musteri: MusteriSemasi.optional(),
    arac_id: kimlik.nullable().optional(),
    arac: AracSemasi.omit({ musteri_id: true }).optional(),
    islem: metin(400).default(''),
    not: metin(4000).default(''),
    kilometre: z.coerce.number().int().min(0).max(10_000_000).nullable().optional(),
    tutar: tutar.default(0),
}).refine((v) => v.musteri_id || v.musteri, { message: 'Müşteri seçilmeli veya yeni müşteri girilmeli' });
/* ARAÇ KOŞULU KALDIRILDI — bilerek.
   Eskiden "araç seçilmeli veya yeni araç girilmeli" koşulu vardı ve
   plakasız iş emri açmayı SUNUCUDA engelliyordu. Araç artık isteğe
   bağlıdır: `arac_id` da `arac` da yoksa iş emri araçsız açılır.
   Müşteri koşulu YERİNDE DURUYOR: iş emri her zaman bir müşteriye aittir. */
export const IsEmriDurumSemasi = z.object({ durum: z.enum(IS_EMRI_DURUMLARI) });
/* ---------- Ödeme ---------- */
export const OdemeSemasi = z.object({
    tutar: tutar.refine((v) => v > 0, 'Tutar sıfırdan büyük olmalı'),
    para_birimi: paraBirimi,
    kur,
    odeme_tipi: z.enum(ODEME_TIPLERI).default('Nakit'),
    tarih: tarih.optional(),
    not: aciklama(400),
});
/* ---------- Kasa ---------- */
export const KasaHareketiSemasi = z.object({
    tarih: tarih,
    saat: saat.default(''),
    tur: z.enum(HAREKET_TURLERI),
    tutar: tutar.refine((v) => v > 0, 'Tutar sıfırdan büyük olmalı'),
    para_birimi: paraBirimi,
    kur,
    odeme_tipi: z.enum(ODEME_TIPLERI).default('Nakit'),
    kategori: kategori(80),
    musteri_id: kimlik.nullable().optional(),
    arac_id: kimlik.nullable().optional(),
    is_emri_id: kimlik.nullable().optional(),
    urun_id: kimlik.nullable().optional(),
    urun_adet: z.coerce.number().min(0).max(100_000).nullable().optional(),
    not: aciklama(1000),
});
export const KasaFiltreSemasi = z.object({
    baslangic: tarih.optional(),
    bitis: tarih.optional(),
    musteri_id: kimlik.optional(),
    is_emri_id: kimlik.optional(),
    tur: z.enum(HAREKET_TURLERI).optional(),
});
/* ---------- Stok & Hizmet ---------- */
export const UrunSemasi = z.object({
    ad: urun(160),
    marka: marka(80),
    kategori: kategori(80),
    alis_fiyat: tutar.default(0),
    satis_fiyat: tutar.default(0),
    /**
     * ALIŞ ve SATIŞ AYRI PARA BİRİMİ TAŞIR — ve kurları farklı davranır.
     *
     * ALIŞ: "120 €'ya aldım." Bu bir GEÇMİŞ olaydır; ödediğin para o günün
     * kuruyla TL'ye çevrilir ve o kur DONAR. Kur sonra yükselse de o parçaya
     * ödediğin lira değişmez — stok değeri ve kâr hesabı bu yüzden doğru kalır.
     *
     * SATIŞ: "150 €'ya satıyorum." Bu bir FİYAT KARARIDIR; dövize bağladıysan
     * kur değiştikçe TL karşılığının da değişmesi İSTENEN davranıştır. Bu
     * yüzden satışın kuru donmaz, günlük kur yenilendiğinde TL karşılığı
     * yeniden hesaplanır (bkz. depo/stok.ts · satisFiyatlariniTazele).
     */
    alis_para_birimi: paraBirimi,
    alis_kur: kur,
    satis_para_birimi: paraBirimi,
    /* Yalnızca YENİ üründe kullanılır: açılış stoğu. Mevcut üründe adet
       buradan değişmez, stok hareketi gerekir. */
    adet: z.coerce.number().min(0).max(1_000_000).default(0),
    birim: metin(20).default('adet'),
    kritik_seviye: z.coerce.number().min(0).max(1_000_000).default(0),
    pasif: z.union([z.literal(0), z.literal(1)]).default(0),
    not: aciklama(1000),
});
export const STOK_SEBEPLERI = ['ekle', 'cikar', 'duzeltme'];
/**
 * STOKTAN SATIŞ — tahsilat biçimi.
 *
 * Kullanıcıya üç düğme olarak çıkar; sunucu alınan tutarı BUNDAN türetir,
 * istemcinin gönderdiği bir rakama güvenmez:
 *   tam    → alınan = satış toplamı
 *   cari   → alınan = 0, tamamı müşterinin hesabına borç yazılır
 *   kismi  → alınan = `alinan` alanı (satış toplamını aşamaz)
 */
export const STOK_SATIS_TAHSILAT = ['tam', 'kismi', 'cari'];
/**
 * STOKTAN SATIŞ
 *
 * ─── İSTEK ───
 * "ürünü stoktan düşerken kimin aldığını, kaç TL'ye aldığını ve cariden ne
 *  kadar düştüğünü seçemiyoruz; direkt stoktan eksiliyor."
 *
 * `birim_fiyat` ve `alinan` KULLANICININ YAZDIĞI sayılardır ve ikisi de
 * `para_birimi` cinsindendir. Satış 150 €'ysa "alınan" da euro olarak
 * girilir — aynı satışta iki farklı para birimi karıştırmak, kullanıcının
 * kafasında da defterde de karışıklık üretirdi.
 *
 * MÜŞTERİ ZORUNLU DEĞİL: boş bırakılırsa tezgâh satışıdır; stok düşer,
 * para kasaya girer, cariye hiçbir şey yazılmaz.
 */
export const StokSatisSemasi = z.object({
    tarih: tarih,
    adet: z.coerce.number().positive('Adet sıfırdan büyük olmalı').max(100_000),
    birim_fiyat: tutar,
    para_birimi: paraBirimi,
    kur,
    musteri_id: kimlik.nullable().optional(),
    arac_id: kimlik.nullable().optional(),
    tahsilat: z.enum(STOK_SATIS_TAHSILAT).default('tam'),
    /* Yalnız `tahsilat === 'kismi'` iken okunur. */
    alinan: tutar.default(0),
    odeme_tipi: z.enum(ODEME_TIPLERI).default('Nakit'),
    not: aciklama(400),
}).refine((v) => v.tahsilat !== 'cari' || !!v.musteri_id, { message: 'Cariye yazmak için müşteri seçilmeli', path: ['musteri_id'] }).refine((v) => v.tahsilat !== 'kismi' || v.alinan > 0, { message: 'Kısmi tahsilatta alınan tutar sıfırdan büyük olmalı', path: ['alinan'] });
export const StokDuzeltSemasi = z.object({
    degisim: z.coerce.number().refine((v) => v !== 0, 'Değişim sıfır olamaz'),
    sebep: z.enum(STOK_SEBEPLERI).default('duzeltme'),
    not: aciklama(400),
});
export const HizmetSemasi = z.object({
    ad: urun(160),
    kategori: kategori(80),
    varsayilan_fiyat: tutar.nullable().optional(),
    aktif: z.union([z.literal(0), z.literal(1)]).default(1),
});
/* ---------- Fiyat kataloğu ---------- */
export const KatalogSemasi = z.object({
    marka: zorunluMetin(60).transform(markaModel),
    model: zorunluMetin(120).transform(markaModel),
    motor_kodu: kod(60),
    stok_hp: z.coerce.number().int().min(0).max(3000).nullable().optional(),
    stok_nm: z.coerce.number().int().min(0).max(5000).nullable().optional(),
    stage1_hp: z.coerce.number().int().min(0).max(3000).nullable().optional(),
    stage1_nm: z.coerce.number().int().min(0).max(5000).nullable().optional(),
    tork_kazanc: metin(40).default(''),
    sure: metin(40).default(''),
    baslangic_fiyat: tutar.nullable().optional(),
    kategori: metin(40).default('stage'),
    etiket: urunBos(60),
    not: aciklama(500),
    aktif: z.union([z.literal(0), z.literal(1)]).default(1),
});
/* ---------- Proforma ---------- */
export const ProformaKalemSemasi = z.object({
    /* Stoktaki ürün ya da katalogdaki hizmet seçildiyse izi burada durur.
       Proforma bir TEKLİF olduğu için stoktan DÜŞMEZ. */
    urun_id: kimlik.nullable().optional(),
    hizmet_id: kimlik.nullable().optional(),
    aciklama: urun(300),
    adet: z.coerce.number().positive().max(100_000).default(1),
    birim_fiyat: tutar.default(0),
    /* Kalem başına para birimi; kur teklif yazıldığı anda donar. Verilmiş bir
       teklifin rakamı kur oynadı diye değişmez. */
    para_birimi: paraBirimi,
    kur,
});
export const ProformaSemasi = z.object({
    belge_no: metin(40).default(''),
    belge_basligi: metin(80).default('PROFORMA FATURA'),
    tarih: tarih,
    is_emri_id: kimlik.nullable().optional(),
    musteri_id: kimlik.nullable().optional(),
    musteri_adi: ad(160),
    telefon: metin(40).default('').transform(telefonDuzen),
    arac: metin(160).default('').transform(bosluklariTopla),
    firma_adi: adBos(160),
    yetkili: adBos(120),
    banka_adi: adBos(120),
    iban: metin(60).default('').transform(ibanDuzen),
    iletisim: metin(240).default('').transform(bosluklariTopla),
    alt_aciklama: aciklama(1000),
    odenen: tutar.default(0),
    kalemler: z.array(ProformaKalemSemasi).min(1, 'En az bir kalem gerekli').max(80),
});
/* ---------- Dosya ---------- */
export const DosyaMetaSemasi = z.object({
    tur: z.enum(DOSYA_TURLERI),
    not: aciklama(400),
});
/* ---------- Ayar ---------- */
export const AyarSemasi = z.object({
    anahtar: zorunluMetin(80),
    deger: z.unknown(),
});
export const TarihAraligiSemasi = z.object({
    baslangic: tarih.optional(),
    bitis: tarih.optional(),
});
//# sourceMappingURL=dogrulama.js.map